import { BookStatus } from '@constants/flight.constant';
import { FlightSort, FlightState } from '@models/store.model';
import { createReducer, on } from '@ngrx/store';
import * as flightActions from '@store/actions/flight.action';
import moment from 'moment';

const initialState: FlightState = {
    search: {
        step: 0,
        bookStatus: BookStatus.ORDER_SUCCESS,
        segments: [],
        itinerary: 1,
        seatClass: '',
        passengers: '',
        sort: 'lowestPrice',
        filter: {
            airlines: [],
            amountFee: null,
            fareModeType: 2,
            flightDepartureTime: {
                combine: [],
                normal: [],
            },
            flightArrivalTime: {
                combine: [],
                normal: [],
            },
            tripType: [],
        },
        fareRules: {},
    },
    selectedFlights: [],
    selectedCombineId: null,
    selectedIndex: 0,
    sharedFlights: {},
    verifyLoading: null,
    fareChanged: null,
    flightDetails: {},
    flightFareRules: {},
};

export const flightReducer = createReducer<FlightState>(
    initialState,
    on(flightActions.changeSearchStep, (state, { step }) => {
        return {
            ...state,
            search: {
                ...state.search,
                step,
            },
        };
    }),
    on(flightActions.updateSegments, (state, { segments, force }) => {
        const selectedFlights = state.selectedFlights;
        let selectedIndex = state.selectedIndex;

        const selected = segments.map((segment, index) => {
            const selectedFlight = selectedFlights[index];
            const isForce =
                force ||
                selectedFlight?.Origin !== segment.Origin ||
                selectedFlight?.Destination !== segment.Destination ||
                !moment(selectedFlight.FlightDate).isSame(segment.FlightDate, 'days');
            if (!isForce) {
                return selectedFlight;
            }

            if (index === 0 && selectedIndex !== 0) {
                selectedIndex = 0;
            }

            return segment;
        });

        return {
            ...state,
            selectedFlights: selected,
            selectedIndex: selectedIndex,
            search: {
                ...state.search,
                segments,
            },
        };
    }),
    on(flightActions.updateSelectedFlights, (state, { selectedFlights }) => {
        const combineId = selectedFlights.find((f) => f.CombineFlightId)?.CombineFlightId || null;
        return {
            ...state,
            selectedFlights: selectedFlights || [],
            selectedCombineId: combineId,
        };
    }),
    on(flightActions.selectFlight, (state, { flight, index }) => {
        const selectedFlights = state.selectedFlights.map((f, i) => {
            if (i === index) {
                return flight;
            }
            return f;
        });
        let newIndex = state.selectedIndex;
        const combineId = selectedFlights.find((f) => f.CombineFlightId)?.CombineFlightId || null;
        const findIndex = selectedFlights.findIndex((f) => !f.FlightData);

        if (findIndex !== -1) {
            newIndex = findIndex;
        }
        return {
            ...state,
            selectedFlights: selectedFlights || [],
            selectedIndex: newIndex,
            selectedCombineId: combineId,
        };
    }),
    on(flightActions.reSelectFlight, (state, { index }) => {
        const selectedFlights = state.selectedFlights.map((f, i) => {
            if ((i === index || index === -1) && f.FlightData) {
                return {
                    Origin: f.Origin,
                    Destination: f.Destination,
                    FlightDate: f.FlightDate,
                };
            }
            return f;
        });

        const findIndex = selectedFlights.findIndex((f) => !f.FlightData);
        const combineId = selectedFlights.find((f) => f.CombineFlightId)?.CombineFlightId || null;
        return {
            ...state,
            selectedFlights: selectedFlights || [],
            selectedIndex: index === -1 ? 0 : findIndex,
            selectedCombineId: combineId,
        };
    }),
    on(flightActions.setSelectedIndex, (state, { index }) => {
        return {
            ...state,
            selectedIndex: index,
        };
    }),
    on(flightActions.updateSearchParams, (state, { seatClass, passengers, itinerary }) => {
        return {
            ...state,
            search: {
                ...state.search,
                seatClass,
                passengers,
                itinerary,
            },
        };
    }),
    on(flightActions.resetSearch, (state) => {
        return {
            ...state,
            search: initialState.search,
            flightDetails: initialState.flightDetails,
            flightFareRules: initialState.flightFareRules,
        };
    }),
    on(flightActions.setSort, (state, { sort }) => {
        return {
            ...state,
            search: {
                ...state.search,
                sort: sort as FlightSort,
            },
        };
    }),
    on(flightActions.setFilter, (state, { filter }) => {
        return {
            ...state,
            search: {
                ...state.search,
                filter: {
                    ...state.search.filter,
                    ...filter,
                },
            },
        };
    }),
    on(flightActions.resetFilter, (state) => {
        return {
            ...state,
            search: {
                ...state.search,
                filter: initialState.search.filter,
            },
        };
    }),
    on(flightActions.cacheShareFlight, (state, { code, ids }) => {
        return {
            ...state,
            sharedFlights: {
                ...state.sharedFlights,
                [ids]: code,
            },
        };
    }),
    on(flightActions.updateVerifyLoading, (state, { loading }) => {
        return {
            ...state,
            verifyLoading: loading,
        };
    }),
    on(flightActions.updateFareChange, (state, { fare }) => {
        return {
            ...state,
            fareChanged: fare,
        };
    }),
    on(flightActions.updateBookStatus, (state, { status }) => {
        return {
            ...state,
            search: {
                ...state.search,
                bookStatus: status,
            },
        };
    }),
    on(flightActions.updateFareRules, (state, { data }) => {
        return {
            ...state,
            search: {
                ...state.search,
                fareRules: data,
            },
        };
    }),
    on(flightActions.setFlightDetail, (state, { detail }) => {
        return {
            ...state,
            flightDetails: {
                ...state.flightDetails,
                ...detail,
            },
        };
    }),
    on(flightActions.setFlightFareRule, (state, { fareRule }) => {
        return {
            ...state,
            flightFareRules: {
                ...state.flightFareRules,
                ...fareRule,
            },
        };
    })
);
